import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
  addDoc,
  serverTimestamp,
  writeBatch,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Theme, ThemePost } from '../types/theme';

export class ThemeService {
  static async getThemes(companyId: string): Promise<Theme[]> {
    try {
      const themesRef = collection(db, 'themes');
      // アーカイブ状態に関係なく全てのテーマを取得
      const q = query(
        themesRef,
        where('companyId', '==', companyId)
      );
      
      const querySnapshot = await getDocs(q);
      
      const themes: Theme[] = [];
      const now = new Date();
      
      // まず全てのテーマを取得し、期間内判定をしながら収集
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const rawEndDate = data.endDate?.toDate() || new Date();
        const rawStartDate = data.startDate?.toDate() || new Date();

        // 日付の包括範囲判定のため、開始日は00:00、終了日は23:59:59.999に正規化する
        const startDate = new Date(rawStartDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(rawEndDate);
        endDate.setHours(23, 59, 59, 999);
        
        themes.push({
          id: doc.id,
          themeId: data.themeId || doc.id,
          title: data.title || '',
          description: data.description || '',
          startDate: startDate,
          endDate: endDate,
          isActive: data.isActive === true, // FirestoreのisActiveフィールドをそのまま使用
          isArchived: data.isArchived === true,
          companyId: data.companyId || companyId,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          statistics: {
            totalPosts: data.statistics?.totalPosts || 0,
          },
        });
      });
      
      // 期間内になった非アクティブテーマを自動的にアクティブ化
      await ThemeService.checkAndActivateFutureThemes(companyId);
      
      // アクティブ化処理後に、isActiveフラグを最新の状態に更新
      // 期間内のテーマを再確認して、最新のものをアクティブとしてマーク
      const themesInPeriod = themes.filter(theme => {
        const started = theme.startDate <= now;
        const notEnded = theme.endDate >= now;
        return started && notEnded && !theme.isArchived;
      });
      
      if (themesInPeriod.length > 0) {
        // 作成日時降順でソート（最新順）
        themesInPeriod.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        const latestThemeId = themesInPeriod[0].id;
        
        // 全てのテーマのisActiveフラグを更新
        themes.forEach(theme => {
          theme.isActive = (theme.id === latestThemeId && !theme.isArchived);
        });
      } else {
        // 期間内のテーマがない場合、全て非アクティブ
        themes.forEach(theme => {
          theme.isActive = false;
        });
      }
      
      // クライアント側でソート（createdAt降順 = 新しい順）
      themes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      return themes;
    } catch (error) {
      console.error('テーマ取得エラー詳細:', error);
      console.error('エラータイプ:', (error as Error).constructor.name);
      console.error('エラーメッセージ:', (error as Error).message);
      console.error('エラースタック:', (error as Error).stack);
      throw error;
    }
  }

  // アクティブなテーマを取得（Flutter版のactiveThemeProviderと同等）
  static async getActiveTheme(companyId: string): Promise<Theme | null> {
    try {
      // 期間内になった非アクティブテーマを自動的にアクティブ化
      await ThemeService.checkAndActivateFutureThemes(companyId);
      
      const themesRef = collection(db, 'themes');
      // アクティブでアーカイブされていないテーマを直接取得
      const q = query(
        themesRef,
        where('companyId', '==', companyId),
        where('isActive', '==', true),
        where('isArchived', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      // アクティブなテーマが複数ある場合は最新のものを返す
      const now = new Date();
      const activeThemes = querySnapshot.docs
        .map(doc => ({ doc, data: doc.data() }))
        .filter(({ data }) => {
          // 期間チェック: startDate <= now <= endDate
          const rawStartDate = data.startDate?.toDate() || new Date();
          const rawEndDate = data.endDate?.toDate() || new Date();
          const startDate = new Date(rawStartDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(rawEndDate);
          endDate.setHours(23, 59, 59, 999);
          const started = startDate <= now;
          const notEnded = endDate >= now;
          return started && notEnded;
        })
        .sort((a, b) => {
          const aTime = a.data.createdAt?.toDate() || new Date();
          const bTime = b.data.createdAt?.toDate() || new Date();
          return bTime.getTime() - aTime.getTime(); // 降順（新しい順）
        });
      
      if (activeThemes.length === 0) {
        return null;
      }
      
      const { doc, data } = activeThemes[0];
      // 返却時も正規化済みのDateを返す
      const rawStartDate = data.startDate?.toDate() || new Date();
      const rawEndDate = data.endDate?.toDate() || new Date();
      const startDate = new Date(rawStartDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(rawEndDate);
      endDate.setHours(23, 59, 59, 999);
      
      return {
        id: doc.id,
        themeId: data.themeId || doc.id,
        title: data.title || '',
        description: data.description || '',
        startDate: startDate,
        endDate: endDate,
        isActive: true,
        isArchived: data.isArchived || false,
        companyId: data.companyId || companyId,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        statistics: {
          totalPosts: data.statistics?.totalPosts || 0,
        },
      };
    } catch (error) {
      console.error('アクティブテーマ取得エラー詳細:', error);
      console.error('エラータイプ:', (error as Error).constructor.name);
      console.error('エラーメッセージ:', (error as Error).message);
      console.error('エラースタック:', (error as Error).stack);
      throw error;
    }
  }

  // テーマ作成（Flutter版のcreateThemeと同等）
  static async createTheme(themeData: {
    title: string;
    description: string;
    startDate: Date;
    endDate: Date;
    companyId: string;
  }): Promise<string> {
    try {
      const now = new Date();
      // 入力された日付を正規化（開始日: 00:00、終了日: 23:59:59.999）
      const normalizedStart = new Date(themeData.startDate);
      normalizedStart.setHours(0, 0, 0, 0);
      const normalizedEnd = new Date(themeData.endDate);
      normalizedEnd.setHours(23, 59, 59, 999);

      const isWithinPeriod = normalizedStart <= now && now <= normalizedEnd;
      
      // 期間内のテーマを作成する場合は既存のテーマを非アクティブ化
      if (isWithinPeriod) {
        await ThemeService.deactivateAllThemes(themeData.companyId);
      }
      
      const themesRef = collection(db, 'themes');
      const docRef = await addDoc(themesRef, {
        title: themeData.title,
        description: themeData.description,
        startDate: normalizedStart,
        endDate: normalizedEnd,
        isActive: isWithinPeriod, // 期間内なら即アクティブ
        isArchived: false,
        companyId: themeData.companyId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        statistics: {
          totalPosts: 0,
        },
      });
      
      return docRef.id;
    } catch (error) {
      console.error('テーマ作成エラー:', error);
      throw error;
    }
  }

  // 全てのテーマを非アクティブ化（内部メソッド）
  static async deactivateAllThemes(companyId: string): Promise<void> {
    try {
      const themesRef = collection(db, 'themes');
      const q = query(
        themesRef,
        where('companyId', '==', companyId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      // バッチ更新で全てのアクティブテーマを非アクティブ化
      const batch = writeBatch(db);
      querySnapshot.docs.forEach((doc) => {
        batch.update(doc.ref, {
          isActive: false,
          updatedAt: serverTimestamp(),
        });
      });
      
      if (querySnapshot.docs.length > 0) {
        await batch.commit();
      }
    } catch (error) {
      console.error('テーマ非アクティブ化エラー:', error);
      throw error;
    }
  }

  // 未来日スタートテーマの自動アクティブ化チェック
  static async checkAndActivateFutureThemes(companyId: string): Promise<void> {
    try {
      const now = new Date();
      const themesRef = collection(db, 'themes');
      
      // 非アーカイブの全テーマを取得
      const q = query(
        themesRef,
        where('companyId', '==', companyId),
        where('isArchived', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      const themes: Array<{ doc: any; data: any }> = [];
      
      // 期間内のテーマをフィルタリング
      querySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const rawStartDate = data.startDate?.toDate() || new Date();
        const rawEndDate = data.endDate?.toDate() || new Date();
        const startDate = new Date(rawStartDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(rawEndDate);
        endDate.setHours(23, 59, 59, 999);
        
        const isWithinPeriod = startDate <= now && now <= endDate;
        if (isWithinPeriod) {
          themes.push({ doc, data });
        }
      });
      
      if (themes.length === 0) return;
      
      // 作成日時降順でソート（最新順）
      themes.sort((a, b) => {
        const aTime = a.data.createdAt?.toDate() || new Date();
        const bTime = b.data.createdAt?.toDate() || new Date();
        return bTime.getTime() - aTime.getTime();
      });
      
      // 最新のテーマを取得
      const latestTheme = themes[0];
      
      // 既にアクティブなテーマが期間内にあるかチェック
      const activeThemeInPeriod = themes.find(t => t.data.isActive === true);
      
      // 既にアクティブなテーマが期間内にあり、それが最新の場合は何もしない
      if (activeThemeInPeriod && activeThemeInPeriod === latestTheme) {
        return;
      }
      
      // 既存のアクティブテーマを非アクティブ化
      await ThemeService.deactivateAllThemes(companyId);
      
      // 最新テーマをアクティブ化
      await updateDoc(latestTheme.doc.ref, {
        isActive: true,
        updatedAt: serverTimestamp(),
      });
      
    } catch (error) {
      console.error('未来日テーマ自動アクティブ化エラー:', error);
      throw error;
    }
  }

  static async getThemePosts(companyId: string, themeId: string): Promise<ThemePost[]> {
    try {
      const postsRef = collection(db, 'posts');
      const q = query(
        postsRef,
        where('companyId', '==', companyId),
        where('type', '==', 'theme'),
        where('themeId', '==', themeId),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      
      const querySnapshot = await getDocs(q);
      const posts: ThemePost[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        posts.push({
          id: doc.id,
          postId: doc.id,
          anonymousId: data.anonymousId || '',
          type: data.type || 'theme',
          category: data.category || '',
          content: data.content || '',
          processedContent: data.processedContent,
          tags: data.tags || [],
          urgency: data.urgency || 'low',
          status: data.status || 'approved',
          themeId: data.themeId || themeId,
          companyId: data.companyId || companyId,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          approvedAt: data.approvedAt?.toDate(),
          approvedBy: data.approvedBy,
          rejectionReason: data.rejectionReason,
        });
      });
      
      return posts;
    } catch (error) {
      console.error('テーマ投稿取得エラー:', error);
      throw error;
    }
  }

  // テーマIDからテーマ情報を取得
  static async getThemeByThemeId(themeId: string): Promise<Theme | null> {
    try {
      
      // まずドキュメントIDで直接取得を試みる
      const docRef = doc(db, 'themes', themeId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        const theme = {
          id: docSnap.id,
          themeId: data.themeId || docSnap.id,
          title: data.title || '',
          description: data.description || '',
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          isActive: data.isActive || false,
          isArchived: data.isArchived || false,
          companyId: data.companyId || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          statistics: {
            totalPosts: data.statistics?.totalPosts || 0,
          },
        };
        
        return theme;
      }
      
      // なければクエリで検索
      const themesRef = collection(db, 'themes');
      const q = query(themesRef, where('themeId', '==', themeId));
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const themeDoc = querySnapshot.docs[0];
      const data = themeDoc.data();
      
      const theme = {
        id: themeDoc.id,
        themeId: data.themeId || themeDoc.id,
        title: data.title || '',
        description: data.description || '',
        startDate: data.startDate?.toDate() || new Date(),
        endDate: data.endDate?.toDate() || new Date(),
        isActive: data.isActive || false,
        isArchived: data.isArchived || false,
        companyId: data.companyId || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        statistics: {
          totalPosts: data.statistics?.totalPosts || 0,
        },
      };
      
      return theme;
    } catch (error) {
      console.error('テーマ取得エラー:', error);
      throw error;
    }
  }

  // テーマをアーカイブ
  static async archiveTheme(themeId: string): Promise<void> {
    try {
      const themeRef = doc(db, 'themes', themeId);
      await updateDoc(themeRef, {
        isArchived: true,
        isActive: false,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('テーマアーカイブエラー:', error);
      throw error;
    }
  }

  // テーマを再表示
  static async unarchiveTheme(themeId: string): Promise<void> {
    try {
      const themeRef = doc(db, 'themes', themeId);
      await updateDoc(themeRef, {
        isArchived: false,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('テーマ再表示エラー:', error);
      throw error;
    }
  }

  // テーマの投稿数を増加
  static async incrementThemePostCount(themeId: string): Promise<void> {
    try {
      const themeRef = doc(db, 'themes', themeId);
      const themeSnap = await getDoc(themeRef);
      
      if (themeSnap.exists()) {
        const currentCount = themeSnap.data()?.statistics?.totalPosts || 0;
        await updateDoc(themeRef, {
          'statistics.totalPosts': currentCount + 1,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('テーマ投稿数更新エラー:', error);
      throw error;
    }
  }
}
